// EntityAvatar — the candidate-facing entity marker (#40). A big entity-TYPE icon
// (WHAT the thing is: a job posting, a course, an application, an engagement…)
// with the organisation's LOGO as a small corner badge (WHOSE it is). It is the
// deliberate inverse of `OrgLogo`: a candidate scans a mixed list by type first
// and sees many different orgs, so the type reads big and the org rides along in
// the corner.
//
// The org badge renders ONLY when an org name/logo is supplied. Employer surfaces
// therefore reuse the same component with the org props omitted and get a plain
// icon chip — their own single org needs no logo. Candidate + candidate-preview
// surfaces pass the org and get icon + logo.
import React, { useState, useEffect } from 'react';
import type { LucideIcon } from 'lucide-react';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { orgPlaceholderColor, orgInitials } from '@/lib/orgLogo';
import { cn } from '@/lib/utils';

type EntityAvatarSize = 'sm' | 'md' | 'lg' | 'xl';
type EntityAvatarTone = 'brand' | 'neutral' | 'green' | 'amber' | 'red' | 'blue';

const SIZES: Record<EntityAvatarSize, { box: string; glyph: string; badge: string; badgeText: string }> = {
    sm: { box: 'w-9 h-9', glyph: 'w-4 h-4', badge: 'w-5 h-5 -bottom-1 -right-1', badgeText: 'text-[8px]' },
    md: { box: 'w-11 h-11', glyph: 'w-5 h-5', badge: 'w-6 h-6 -bottom-1 -right-1', badgeText: 'text-[9px]' },
    lg: { box: 'w-12 h-12', glyph: 'w-6 h-6', badge: 'w-7 h-7 -bottom-1.5 -right-1.5', badgeText: 'text-[10px]' },
    xl: { box: 'w-14 h-14', glyph: 'w-7 h-7', badge: 'w-8 h-8 -bottom-1.5 -right-1.5', badgeText: 'text-[11px]' },
};

const TONES: Record<EntityAvatarTone, string> = {
    brand: 'bg-[#A4C3B2]/30 text-[#1A4D4F]',
    neutral: 'bg-gray-100 text-gray-500',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
    blue: 'bg-blue-50 text-blue-600',
};

interface EntityAvatarProps {
    /** The entity-type glyph (Briefcase = job posting, BookOpen = course, …). */
    icon: LucideIcon;
    tone?: EntityAvatarTone;
    size?: EntityAvatarSize;
    /** Org identity for the corner badge. Omit both → plain icon (employer view). */
    orgName?: string;
    orgLogoUrl?: string | null;
    className?: string;
}

export const EntityAvatar: React.FC<EntityAvatarProps> = ({
    icon: Icon, tone = 'brand', size = 'md', orgName, orgLogoUrl, className,
}) => {
    const s = SIZES[size];
    const src = resolveMediaUrl(orgLogoUrl);
    const hasOrg = !!(orgName || orgLogoUrl);
    // Fall back to name-initials if the logo image fails; reset when URL changes.
    const [failed, setFailed] = useState(false);
    useEffect(() => setFailed(false), [src]);

    let badge: React.ReactNode = null;
    if (hasOrg) {
        if (src && !failed) {
            badge = (
                <img
                    src={src}
                    alt={orgName ? `${orgName} logotyp` : 'Organisationslogotyp'}
                    onError={() => setFailed(true)}
                    className="w-full h-full rounded-full object-contain bg-white"
                />
            );
        } else {
            const { bg, fg } = orgPlaceholderColor(orgName || '?');
            badge = (
                <span
                    style={{ backgroundColor: bg, color: fg }}
                    className={cn('w-full h-full flex items-center justify-center rounded-full font-semibold select-none', s.badgeText)}
                >
                    {orgInitials(orgName)}
                </span>
            );
        }
    }

    return (
        <span className={cn('relative inline-block flex-shrink-0', s.box, className)}>
            <span className={cn('w-full h-full flex items-center justify-center rounded-xl', TONES[tone])}>
                <Icon className={s.glyph} />
            </span>
            {badge && (
                <span
                    className={cn(
                        'absolute flex items-center justify-center rounded-full bg-white border border-gray-200 shadow-sm overflow-hidden',
                        s.badge,
                    )}
                    aria-label={orgName ? `${orgName} logotyp` : undefined}
                >
                    {badge}
                </span>
            )}
        </span>
    );
};

export default EntityAvatar;
